import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "doc-check" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = {
  key: 0,
  class: "info-banner clean"
}
const _hoisted_4 = {
  key: 0,
  class: "issue-group"
}
const _hoisted_5 = {
  key: 1,
  class: "issue-group"
}
const _hoisted_6 = {
  key: 2,
  class: "issue-group"
}
const _hoisted_7 = {
  key: 1,
  class: "error-banner"
}

import { ref, watch } from 'vue';
import { scanDocumentTags } from '@/services/documentScanner';
import { validateTags, type ValidationResult } from '@/services/tagValidator';
import {
  sourceTypeLabels,
  type DocumentTemplateSourceType,
  type SupportedTags,
} from '@/types/SupportedTags';

const OPEN = '{' + '{';
const CLOSE = '}' + '}';


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentCheckPanel',
  props: {
    tags: {}
  },
  setup(__props: any) {

const props = __props;

const scanning = ref(false);
const result = ref<ValidationResult | null>(null);
const errorMessage = ref<string | null>(null);

// Same string-concat trick as elsewhere — Vue mustaches don't handle literal
// `{{` inside a `{{ ... }}` binding.
function formatScalar(tag: string): string {
  return `${OPEN}${tag}${CLOSE}`;
}

const sourceTypeLabel = ref<string>('');
watch(
  () => props.tags?.sourceType,
  (v: DocumentTemplateSourceType | undefined) => {
    sourceTypeLabel.value = v !== undefined ? sourceTypeLabels[v] : '';
    // Also clear stale results — validating against a different inventory
    // would flag every previously-clean tag.
    result.value = null;
    errorMessage.value = null;
  },
  { immediate: true },
);

async function onCheck(): Promise<void> {
  if (!props.tags) return;
  scanning.value = true;
  errorMessage.value = null;
  try {
    const found = await scanDocumentTags();
    result.value = validateTags(found, props.tags);
  } catch (e) {
    errorMessage.value = e instanceof Error
      ? `Scan failed: ${e.message}`
      : 'Scan failed.';
    result.value = null;
  } finally {
    scanning.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      type: "button",
      class: "check-btn",
      disabled: scanning.value || !__props.tags,
      onClick: onCheck
    }, _toDisplayString(scanning.value ? 'Scanning document…' : 'Review possible tag issues'), 9 /* TEXT, PROPS */, _hoisted_2),
    (result.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (result.value.isClean)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _cache[0] || (_cache[0] = _createTextVNode(" No obvious tag issues found for ", -1 /* CACHED */)),
                _createElementVNode("strong", null, _toDisplayString(sourceTypeLabel.value), 1 /* TEXT */),
                _cache[1] || (_cache[1] = _createTextVNode(". ", -1 /* CACHED */))
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "warning-banner" }, " Possibly unsupported tags — verify these before uploading. Some may be false positives (e.g. plain text that happens to look like a tag). ", -1 /* CACHED */)),
                (result.value.unknownScalars.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _cache[2] || (_cache[2] = _createElementVNode("div", { class: "issue-label" }, "Scalar tags", -1 /* CACHED */)),
                      _createElementVNode("ul", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(result.value.unknownScalars, (tag) => {
                          return (_openBlock(), _createElementBlock("li", { key: tag }, [
                            _createElementVNode("code", null, _toDisplayString(formatScalar(tag)), 1 /* TEXT */)
                          ]))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ]))
                  : _createCommentVNode("v-if", true),
                (result.value.unknownRegions.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _cache[4] || (_cache[4] = _createElementVNode("div", { class: "issue-label" }, "Region names", -1 /* CACHED */)),
                      _createElementVNode("ul", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(result.value.unknownRegions, (name) => {
                          return (_openBlock(), _createElementBlock("li", { key: name }, [
                            _createElementVNode("code", null, "{{TableStart:" + _toDisplayString(name) + "}}", 1 /* TEXT */),
                            _cache[3] || (_cache[3] = _createTextVNode(" / ", -1 /* CACHED */)),
                            _createElementVNode("code", null, "{{TableEnd:" + _toDisplayString(name) + "}}", 1 /* TEXT */)
                          ]))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ]))
                  : _createCommentVNode("v-if", true),
                (result.value.unknownColumns.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "issue-label" }, "Column tags", -1 /* CACHED */)),
                      _createElementVNode("ul", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(result.value.unknownColumns, (col) => {
                          return (_openBlock(), _createElementBlock("li", { key: col }, [
                            _createElementVNode("code", null, "{{" + _toDisplayString(col) + "}}", 1 /* TEXT */)
                          ]))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ]))
                  : _createCommentVNode("v-if", true)
              ], 64 /* STABLE_FRAGMENT */))
        ], 64 /* STABLE_FRAGMENT */))
      : _createCommentVNode("v-if", true),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(errorMessage.value), 1 /* TEXT */))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})